// src/components/RefundForm.js
// The refund fields, shared by the « new refund » modal (Refunds page), the
// ticket's Refunds tab and the refund detail. Controlled: the parent owns the
// form object (utils/refunds.js builds and serialises it). The ticket's
// declared values are offered as one-click fills, never applied silently: a
// declared value is what the parcel was worth, not what the carrier granted.

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  REFUND_REASONS,
  REFUND_SETTLEMENTS,
  REFUND_SOURCES,
  REFUND_STATUSES,
  formatRefundMoney,
} from '../utils/refunds';
import './Refunds.css';

const RefundForm = ({ form, onChange, declared, ticketOrder, idPrefix = 'rfm' }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const set = (field) => (event) => onChange(field, event.target.value);
  const id = (name) => `${idPrefix}-${name}`;

  // Keep a legacy value selectable so an existing refund never silently changes.
  const withCurrent = (values, current) => (current && !values.includes(current) ? [...values, current] : values);

  const suggestions = [
    { field: 'shipping_cost_refund_amount', value: declared?.shipping_price, label: t('refunds.useDeclaredShipping', 'Frais de port {{amount}}', { amount: formatRefundMoney(declared?.shipping_price, declared?.currency, lang) }) },
    { field: 'content_refund_amount', value: declared?.goods_value, label: t('refunds.useDeclaredGoods', 'Valeur marchandise {{amount}}', { amount: formatRefundMoney(declared?.goods_value, declared?.currency, lang) }) },
    { field: 'insurance_refund_amount', value: declared?.insurance_value, label: t('refunds.useDeclaredInsurance', 'Assurance {{amount}}', { amount: formatRefundMoney(declared?.insurance_value, declared?.currency, lang) }) },
  ].filter((s) => Number(s.value || 0) > 0 && String(form[s.field] || '') !== String(s.value));

  return (
    <div className="rfm-form">
      <section className="rfm-section">
        <h4 className="rfm-section-title">{t('refunds.sectionDecision', 'Décision')}</h4>
        <div className="rfm-grid">
          <label className="rfm-field" htmlFor={id('status')}>
            <span>{t('refunds.status', 'Statut')}</span>
            <select id={id('status')} value={form.status} onChange={set('status')}>
              {withCurrent(REFUND_STATUSES, form.status).map((value) => (
                <option key={value} value={value}>{t(`refunds.statuses.${value}`, value)}</option>
              ))}
            </select>
          </label>
          <label className="rfm-field" htmlFor={id('source')}>
            <span>{t('refunds.source', 'Source')}</span>
            <select id={id('source')} value={form.source} onChange={set('source')}>
              {withCurrent(REFUND_SOURCES, form.source).map((value) => (
                <option key={value} value={value}>{t(`refunds.sources.${value}`, value)}</option>
              ))}
            </select>
          </label>
          <label className="rfm-field" htmlFor={id('reason')}>
            <span>{t('refunds.reason', 'Motif')}</span>
            <select id={id('reason')} value={form.reason} onChange={set('reason')}>
              {withCurrent(REFUND_REASONS, form.reason).map((value) => (
                <option key={value} value={value}>{t(`refunds.reasons.${value}`, value)}</option>
              ))}
            </select>
          </label>
          <label className="rfm-field" htmlFor={id('settlement')}>
            <span>{t('refunds.settlement', 'Règlement')}</span>
            <select id={id('settlement')} value={form.settlement} onChange={set('settlement')}>
              {withCurrent(REFUND_SETTLEMENTS, form.settlement).map((value) => (
                <option key={value} value={value}>{t(`refunds.settlements.${value}`, value)}</option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="rfm-section">
        <h4 className="rfm-section-title">{t('refunds.sectionAmounts', 'Montants')}</h4>
        <div className="rfm-grid">
          {[
            ['shipping_cost_refund_amount', t('refunds.shippingAmount', 'Transport')],
            ['content_refund_amount', t('refunds.contentAmount', 'Contenu')],
            ['insurance_refund_amount', t('refunds.insuranceAmount', 'Assurance')],
          ].map(([field, label]) => (
            <label key={field} className="rfm-field" htmlFor={id(field)}>
              <span>{label}</span>
              <input id={id(field)} type="number" min="0" step="0.01" inputMode="decimal" placeholder="—"
                value={form[field]} onChange={set(field)} />
            </label>
          ))}
          <label className="rfm-field rfm-field--narrow" htmlFor={id('currency')}>
            <span>{t('refunds.currency', 'Devise')}</span>
            <input id={id('currency')} type="text" maxLength={3} value={form.currency}
              onChange={(e) => onChange('currency', e.target.value.toUpperCase())} />
          </label>
        </div>
        {suggestions.length > 0 && (
          <div className="rfm-suggestions">
            <span>{t('refunds.useDeclaredHint', 'Déclaré sur le ticket :')}</span>
            {suggestions.map((s) => (
              <button key={s.field} type="button" className="rfm-chip" onClick={() => onChange(s.field, String(s.value))}>
                {s.label}
              </button>
            ))}
          </div>
        )}
        <p className="rfm-hint">
          {t('refunds.amountsHint', 'Laissez vide tant que le transporteur n’a pas donné le montant : le remboursement affiche « montant à confirmer ».')}
        </p>
      </section>

      <section className="rfm-section">
        <h4 className="rfm-section-title">{t('refunds.sectionReferences', 'Références')}</h4>
        <div className="rfm-grid">
          <label className="rfm-field" htmlFor={id('carrier_reference')}>
            <span>{t('refunds.carrierReference', 'Réf. dossier transporteur')}</span>
            <input id={id('carrier_reference')} type="text" maxLength={80} value={form.carrier_reference}
              onChange={set('carrier_reference')} />
          </label>
          <label className="rfm-field" htmlFor={id('order_reference')}>
            <span>{t('refunds.orderReference', 'Commande')}</span>
            <input id={id('order_reference')} type="text" maxLength={100} value={form.order_reference}
              placeholder={ticketOrder ? t('refunds.orderFromTicket', 'Du ticket : {{order}}', { order: ticketOrder }) : t('refunds.orderPlaceholder', 'Référence de commande')}
              onChange={set('order_reference')} />
          </label>
        </div>
        <label className="rfm-check" htmlFor={id('has_insurance')}>
          <input id={id('has_insurance')} type="checkbox" checked={form.has_insurance}
            onChange={(e) => onChange('has_insurance', e.target.checked)} />
          {t('refunds.hasInsurance', 'Colis assuré')}
        </label>
        {form.has_insurance && (
          <div className="rfm-grid">
            <label className="rfm-field" htmlFor={id('insurance_value')}>
              <span>{t('refunds.insuranceValue', 'Valeur assurée')}</span>
              <input id={id('insurance_value')} type="number" min="0" step="0.01" value={form.insurance_value}
                onChange={set('insurance_value')} />
            </label>
            <label className="rfm-field" htmlFor={id('insurance_price')}>
              <span>{t('refunds.insurancePrice', 'Prix de l’assurance')}</span>
              <input id={id('insurance_price')} type="number" min="0" step="0.01" value={form.insurance_price}
                onChange={set('insurance_price')} />
            </label>
          </div>
        )}
        <label className="rfm-field" htmlFor={id('note')}>
          <span>{t('refunds.note', 'Note')}</span>
          <textarea id={id('note')} rows={3} maxLength={2000} value={form.note}
            placeholder={t('refunds.notePlaceholder', 'Note interne sur ce remboursement')} onChange={set('note')} />
        </label>
      </section>
    </div>
  );
};

export default RefundForm;
